// src/App.js
import React, { useState, useEffect } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import './css/global.css';
import Header from './components/Header';
import Footer from './components/Footer';
import Timeline from './components/Timeline';
import TimeMachine from './components/TimeMachine';
import { ArrowUp } from 'lucide-react';
import javaVersions from './data';
import { extractFeatureRecords } from './components/utils';
import './css/App.css';
import { Analytics } from "@vercel/analytics/react"

function App() {
  const featureMap = extractFeatureRecords(javaVersions);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [theme, setTheme] = useState('light');
  const [showFilters, setShowFilters] = useState(false);

  // Filter state
  const [query, setQuery] = useState('');
  const [phaseFilters, setPhaseFilters] = useState({
    Incubator: true,
    Preview: true,
    Final: true
  });
  const [ltsOnly, setLtsOnly] = useState(false);

  // Scroll-to-top button visibility
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Sync theme to <html> element
  useEffect(() => {
    document.documentElement.classList.remove('theme-light', 'theme-dark');
    document.documentElement.classList.add(`theme-${theme}`);
  }, [theme]);

  // Show button when scrolled to bottom
  useEffect(() => {
    const handleScroll = () => {
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight;
      setShowScrollTop(scrolledToBottom);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleTheme = () => setTheme(t => (t === 'light' ? 'dark' : 'light'));
  const handleToggleFilters = () => setShowFilters(f => !f);
  const handleQueryChange = q => setQuery(q);
  const handleTogglePhase = phase =>
    setPhaseFilters(pf => ({ ...pf, [phase]: !pf[phase] }));
  const handleToggleLts = () => setLtsOnly(l => !l);

  // Apply filters to releases & features
  const filteredVersions = javaVersions
    .filter(v => !ltsOnly || ['8', '11', '17', '21'].includes(v.version))
    .map(v => ({
      ...v,
      features: v.features.filter(feat => {
        const raw = feat.metadata.feature_name;
        const name = raw.replace(/\s*\((Preview|Incubator)\)$/i, '');
        let phase = 'Final';
        if (/\(Preview\)$/i.test(raw)) phase = 'Preview';
        else if (/\(Incubator\)$/i.test(raw)) phase = 'Incubator';
        return (
          phaseFilters[phase] &&
          (!query || name.toLowerCase().includes(query.toLowerCase()))
        );
      })
    }))
    .filter(v => v.features.length > 0);

  return (
    <ErrorBoundary>
      <div className="App">
        <Header
          theme={theme}
          onToggleTheme={handleToggleTheme}
          showFilters={showFilters}
          onToggleFilters={handleToggleFilters}
          query={query}
          onQueryChange={handleQueryChange}
          phaseFilters={phaseFilters}
          onTogglePhase={handleTogglePhase}
          ltsOnly={ltsOnly}
          onToggleLts={handleToggleLts}
        />
        <main>
          <Timeline
            versions={filteredVersions}
            onFeatureClick={setSelectedFeature}
            ltsOnly={ltsOnly}
          />
          {selectedFeature && (
            <TimeMachine
              featureMap={featureMap}
              selected={selectedFeature}
              onClose={() => setSelectedFeature(null)}
            />
          )}
        </main>
        <Footer />

        {/* Scroll to top button */}
        {showScrollTop && (
          <button
            className="scroll-top-button"
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            <ArrowUp />
          </button>
        )}
      </div>
      <Analytics/>
    </ErrorBoundary>
  );
}

export default App;
